const DISPOSED = "The stream scope has been disposed";

/**
 * Ownership for a stream and everything that happens around it.
 *
 * An abort controller stops the transport but cannot stop a REST response that was already in
 * flight from writing into the owner that replaced it. A lease is checked after every await, so
 * work started for a previous owner cannot land on the current one.
 */
export const createSseScope = () => {
  let currentLease = null;
  let disposed = false;

  const invalidate = (reason) => {
    const previous = currentLease;
    currentLease = null;
    if (previous) {
      previous.invalidate(reason);
    }
  };

  return {
    begin() {
      if (disposed) {
        throw new Error(DISPOSED);
      }
      invalidate("superseded");

      const controller = new AbortController();
      let valid = true;
      let endedBecause = null;

      const lease = {
        signal: controller.signal,
        isCurrent: () => valid && !disposed,
        /**
         * Why this lease stopped being current, or null while it still is. A turn replaced by a
         * newer one must leave the shared UI state to its replacement; a turn the user stopped
         * has to clear that state itself, and the two are otherwise indistinguishable.
         */
        endedBecause: () => endedBecause,
        /**
         * Ends this lease only. Routing it through the scope let a stale holder invalidate the
         * lease that had already replaced it, which is the opposite of what ownership is for.
         */
        abort(reason) {
          if (currentLease === lease) {
            invalidate(reason);
            return;
          }
          lease.invalidate(reason);
        },
        invalidate(reason) {
          if (!valid) {
            return;
          }
          valid = false;
          endedBecause = reason ?? "cancelled";
          controller.abort(reason);
        },
      };

      currentLease = lease;
      return lease;
    },

    abortCurrent(reason = "cancelled") {
      invalidate(reason);
    },

    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      invalidate("disposed");
    },

    isDisposed: () => disposed,
  };
};
